// VoiceSampleUpload.jsx - WAV Only Version
// Standalone component for voice sample recording and uploading
// Modified to support ONLY .wav format for recordings and uploads

import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  IconButton,
  Slider,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  VolumeUp as VolumeIcon,
  Timer as TimerIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

import { useGetPreSignedUrlVoiceQuery } from '../../../redux/thunks/files/filespresignurlvoice';

const VoiceSampleUpload = ({ 
  businessId, 
  businessName, 
  accountType = 'free',
  existingVoice = null,
  onSuccess, 
  onCancel,
  onError,
  supportedFormat = '.wav',
  audioMimeType = 'audio/wav',
  validateFile = null,
  formatDescription = 'WAV audio files only'
}) => {
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  // Audio file state (separate from upload state)
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioType, setAudioType] = useState('wav'); // Always WAV
  const [audioSource, setAudioSource] = useState(''); // 'recording' or 'upload'
  
  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  
  // Form state
  const [voiceName, setVoiceName] = useState(existingVoice?.voiceName || '');
  const [voiceDescription, setVoiceDescription] = useState(existingVoice?.voiceDescription || '');
  const [sampleScript, setSampleScript] = useState('');
  const [useCustomScript, setUseCustomScript] = useState(false);
  
  // Upload state - separate from file selection
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  
  // State for triggering presigned URL fetch - only when actually uploading
  const [shouldFetchPresignedUrl, setShouldFetchPresignedUrl] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const playbackTimerRef = useRef(null);
  
  const token = useSelector((state) => state.auth?.token);

  // WAV-only quotas and limits
  const quotaLimits = {
    free: { 
      maxSamples: 1, 
      maxDuration: 120, // 2 minutes
      maxFileSize: 15 * 1024 * 1024, // 15MB (WAV files are larger)
      supportedFormats: ['wav'] // Only WAV
    },
    premium: { 
      maxSamples: 5, 
      maxDuration: 300, // 5 minutes
      maxFileSize: 75 * 1024 * 1024, // 75MB (WAV files are larger)
      supportedFormats: ['wav'] // Only WAV
    },
    enterprise: { 
      maxSamples: -1, 
      maxDuration: 600, // 10 minutes
      maxFileSize: 150 * 1024 * 1024, // 150MB (WAV files are larger)
      supportedFormats: ['wav'] // Only WAV
    }
  };

  const currentLimits = quotaLimits[accountType] || quotaLimits.free;

  // Sample scripts based on business type
  const sampleScripts = {
    restaurant: "Welcome to our restaurant! We're excited to serve you our freshly prepared dishes made with locally sourced ingredients. Our menu features both classic favorites and innovative creations that showcase the best of seasonal cooking. Thank you for choosing us for your dining experience today.",
    
    retail: "Hello and welcome to our store! We're here to help you find exactly what you're looking for. Our knowledgeable team is always ready to assist you with any questions about our products. We appreciate your business and look forward to serving you with quality items and exceptional service.",
    
    service: "Thank you for considering our services. We're committed to providing you with professional, reliable, and high-quality work that exceeds your expectations. Our experienced team takes pride in delivering results that make a real difference. We look forward to working with you and building a lasting partnership.",
    
    healthcare: "Welcome to our practice. We're dedicated to providing you with compassionate, personalized care that addresses your unique health needs. Our team is here to support your wellness journey with expertise, understanding, and respect. Thank you for trusting us with your healthcare.",
    
    general: "Hello and welcome! We're thrilled you're here and excited to share what makes our business special. Our team is passionate about what we do and committed to providing you with exceptional service and value. We appreciate your time and trust, and we're here to help in any way we can."
  };

  // Get presigned URL only when uploading
  const { 
    data: uploadData, 
    isLoading: isAudioUrlLoading, 
    error: audioUrlError,
    refetch: refetchPresignedUrl 
  } = useGetPreSignedUrlVoiceQuery({
    eventType: 'BusinessVoiceSample',
    audioType: 'wav', // Always WAV
    businessId: businessId,
    voiceName: voiceName.trim(),
    voiceDescription: voiceDescription.trim()
  }, {
    skip: !shouldFetchPresignedUrl || !audioBlob || !voiceName.trim()
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
      }
    };
  }, [audioUrl]);

  // Handle upload completion after getting presigned URL
  useEffect(() => {
    if (uploadData && shouldFetchPresignedUrl && audioBlob) {
      handleS3Upload();
    }
  }, [uploadData, shouldFetchPresignedUrl, audioBlob]);

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Function to convert audio to WAV format
  const convertToWav = async (audioBlob) => {
    return new Promise((resolve, reject) => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const fileReader = new FileReader();
      
      fileReader.onload = async (e) => {
        try {
          const arrayBuffer = e.target.result;
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          // Convert to WAV
          const wavBlob = bufferToWav(audioBuffer);
          resolve(wavBlob);
        } catch (error) {
          reject(error);
        }
      };
      
      fileReader.onerror = () => reject(new Error('Failed to read audio file'));
      fileReader.readAsArrayBuffer(audioBlob);
    });
  };

  // Helper function to convert AudioBuffer to WAV blob
  const bufferToWav = (buffer) => {
    const length = buffer.length;
    const sampleRate = buffer.sampleRate;
    const numberOfChannels = buffer.numberOfChannels;
    const bytesPerSample = 2; // 16-bit
    const blockAlign = numberOfChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = length * blockAlign;
    const bufferSize = 44 + dataSize;
    
    const arrayBuffer = new ArrayBuffer(bufferSize);
    const view = new DataView(arrayBuffer);
    
    // WAV header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, bufferSize - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);
    
    // Convert audio data
    let offset = 44;
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const channelData = buffer.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        const sample = Math.max(-1, Math.min(1, channelData[i]));
        view.setInt16(offset, sample * 0x7FFF, true);
        offset += 2;
      }
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  // Function to upload voice sample to S3
  const uploadVoiceSampleToS3 = async (audioBlob, presignedData, metadata) => {
    if (!audioBlob || !presignedData?.url) {
      throw new Error('Missing audio data or upload URL');
    }

    try {
      const response = await fetch(presignedData.url, {
        method: 'PUT',
        body: audioBlob,
        headers: {
          'Content-Type': 'audio/wav', // Always WAV
          'x-amz-meta-eventtype': metadata.eventType || 'BusinessVoiceSample',
          'x-amz-meta-audiotype': 'wav', // Always WAV
          'x-amz-meta-voicename': metadata.voiceName || 'unnamed'
        }
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('S3 upload error:', error);
      throw error;
    }
  };

  // Start recording - MODIFIED to record in WAV compatible format
  const startRecording = async () => {
    try {
      setError(null);
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 44100, // Standard WAV sample rate
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      // Try to use WAV directly, fallback to WebM for conversion
      let options = { mimeType: 'audio/wav' };
      
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        // Fallback to WebM and convert to WAV later
        options = { mimeType: 'audio/webm;codecs=opus' };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          options = { mimeType: 'audio/webm' };
        }
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      chunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const recordedBlob = new Blob(chunksRef.current, { type: options.mimeType });
        
        try {
          let finalBlob;
          
          // If not already WAV, convert to WAV
          if (!options.mimeType.includes('wav')) {
            setError('Converting to WAV format...');
            finalBlob = await convertToWav(recordedBlob);
            setError(null);
          } else {
            finalBlob = recordedBlob;
          }
          
          setAudioFile(finalBlob, 'wav', 'recording');
        } catch (conversionError) {
          console.error('WAV conversion error:', conversionError);
          setError('Failed to convert recording to WAV format. Please try again.');
        }
        
        // Clean up stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start recording timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1;
          // Auto-stop at max duration
          if (newTime >= currentLimits.maxDuration) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
      
    } catch (err) {
      setError('Could not access microphone. Please ensure microphone permissions are granted and try again.');
      console.error('Microphone access error:', err);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  // MODIFIED: Handle file selection - WAV ONLY
  const handleFileSelection = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError(null);

    // Validate WAV file using custom validator if provided
    if (validateFile) {
      const validation = validateFile(file);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }
    } else {
      // Default WAV validation
      if (!file.name.toLowerCase().endsWith('.wav')) {
        setError('Only WAV (.wav) files are supported. Please convert your audio to WAV format before uploading.');
        return;
      }
      
      if (file.type && !file.type.includes('wav')) {
        setError('Invalid file type. Only WAV audio files are accepted.');
        return;
      }
    }

    // Validate file size
    if (file.size > currentLimits.maxFileSize) {
      const maxSizeMB = Math.round(currentLimits.maxFileSize / (1024 * 1024));
      setError(`File too large. Maximum size for ${accountType} accounts: ${maxSizeMB}MB`);
      return;
    }

    // Set the audio file - always WAV
    setAudioFile(file, 'wav', 'upload');
    
    // Reset the file input
    event.target.value = '';
  };

  // NEW: Common function to set audio file (from recording or upload)
  const setAudioFile = (blob, type, source) => {
    // Clean up previous audio URL
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    
    setAudioBlob(blob);
    setAudioType('wav'); // Always WAV
    setAudioSource(source);
    
    const newAudioUrl = URL.createObjectURL(blob);
    setAudioUrl(newAudioUrl);
    
    // Get audio duration for uploaded files
    if (source === 'upload') {
      const audio = new Audio(newAudioUrl);
      audio.addEventListener('loadedmetadata', () => {
        setAudioDuration(audio.duration);
        
        // Validate duration
        if (audio.duration > currentLimits.maxDuration) {
          setError(`Audio too long. Maximum duration for ${accountType} accounts: ${Math.floor(currentLimits.maxDuration / 60)} minutes`);
          return;
        }
      });
    } else {
      // For recordings, duration is based on recording time
      setAudioDuration(recordingTime);
    }
  };

  // Play/pause audio
  const togglePlayback = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
      }
    } else {
      audioRef.current.play();
      setIsPlaying(true);
      
      // Start playback timer
      playbackTimerRef.current = setInterval(() => {
        if (audioRef.current) {
          setPlaybackTime(audioRef.current.currentTime);
        }
      }, 100);
    }
  };

  // Handle audio events
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setPlaybackTime(0);
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  // Seek audio
  const handleSeek = (event, newValue) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newValue;
      setPlaybackTime(newValue);
    }
  };

  // Clear audio
  const clearAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioType('wav');
    setAudioSource('');
    setPlaybackTime(0);
    setAudioDuration(0);
    setIsPlaying(false);
    setRecordingTime(0);
    setShouldFetchPresignedUrl(false);
    
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
    }
  };

  // SEPARATED: Upload logic - only triggered when user clicks upload
  const submitVoiceSample = async () => {
    if (!audioBlob || !voiceName.trim()) {
      setError('Please provide both audio recording and voice name.');
      return;
    }

    if (voiceName.length < 2 || voiceName.length > 50) {
      setError('Voice name must be between 2 and 50 characters.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Trigger presigned URL fetch for upload
      setShouldFetchPresignedUrl(true);
      
      // The actual upload will happen in the useEffect when uploadData is available
      
    } catch (err) {
      setError(err.message);
      setUploading(false);
      onError && onError(err);
    }
  };

  // Handle S3 upload after getting presigned URL
  const handleS3Upload = async () => {
    try {
      if (!uploadData || !audioBlob) {
        throw new Error('Missing upload data or audio file');
      }

      // Upload to S3 using presigned URL
      await uploadVoiceSampleToS3(audioBlob, uploadData, {
        eventType: 'BusinessVoiceSample',
        audioType: 'wav', // Always WAV
        voiceName: voiceName.trim()
      });

      // Success - call callback with voice data
      const voiceData = {
        voiceId: uploadData.reservation?.id || uploadData.reservationId,
        voiceName: voiceName.trim(),
        voiceDescription: voiceDescription.trim(),
        status: 'processing',
        provider: 'elevenlabs',
        createdAt: new Date().toISOString(),
        metadata: {
          sampleDuration: audioSource === 'recording' ? recordingTime : audioDuration,
          audioFormat: 'wav', // Always WAV
          fileSize: audioBlob.size,
          source: audioSource
        }
      };

      setUploading(false);
      setShouldFetchPresignedUrl(false);
      onSuccess && onSuccess(voiceData);

    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message);
      setUploading(false);
      setShouldFetchPresignedUrl(false);
      onError && onError(err);
    }
  };

  return (
    <Box>
      {/* WAV Format Notice */}
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon />
          <Typography variant="body2">
            <strong>WAV Format Only:</strong> This system only supports WAV (.wav) audio files. 
            Recordings will be automatically converted to WAV format for optimal quality.
          </Typography>
        </Box>
      </Alert>

      {/* Account Info */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>{accountType.charAt(0).toUpperCase() + accountType.slice(1)} Plan:</strong>{' '}
          {currentLimits.maxSamples === -1 ? 'Unlimited' : currentLimits.maxSamples} voice samples, 
          {Math.floor(currentLimits.maxDuration / 60)} min max duration, 
          {Math.round(currentLimits.maxFileSize / (1024 * 1024))}MB max file size (WAV format)
        </Typography>
      </Alert>

      {/* Voice Sample Info */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Voice Name *"
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            placeholder="e.g., Professional Voice"
            helperText="Give your voice a memorable name (2-50 characters)"
            error={voiceName.length > 0 && (voiceName.length < 2 || voiceName.length > 50)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Description (Optional)"
            value={voiceDescription}
            onChange={(e) => setVoiceDescription(e.target.value)}
            placeholder="e.g., Warm and welcoming tone"
            helperText="Describe the voice characteristics"
          />
        </Grid>
      </Grid>

      {/* Sample Script Section */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2">Suggested Reading Script</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={useCustomScript}
                  onChange={(e) => setUseCustomScript(e.target.checked)}
                />
              }
              label="Custom Script"
            />
          </Box>
          
          {useCustomScript ? (
            <TextField
              fullWidth
              multiline
              rows={4}
              value={sampleScript}
              onChange={(e) => setSampleScript(e.target.value)}
              placeholder="Write your own script for voice cloning..."
              helperText="Write a script that represents how you want your voice to sound"
            />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {sampleScripts.general}
            </Typography>
          )}
          
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Tips:</strong> Speak clearly and naturally. Include varied sentence lengths and emotions. 
              Record in a quiet environment for best WAV quality results.
            </Typography>
          </Alert>
        </CardContent>
      </Card>

      {/* Recording Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Option 1: Record Voice Sample (WAV Format)
        </Typography>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Recording will be automatically saved in WAV format for optimal voice cloning quality.
          </Typography>
        </Alert>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button
            variant={isRecording ? "contained" : "outlined"}
            color={isRecording ? "error" : "primary"}
            startIcon={isRecording ? <StopIcon /> : <MicIcon />}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={uploading}
            size="large"
          >
            {isRecording ? 'Stop Recording' : 'Start Recording (WAV)'}
          </Button>
          
          {isRecording && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon color="error" />
              <Typography variant="body2" color="error">
                {formatTime(recordingTime)} / {formatTime(currentLimits.maxDuration)}
              </Typography>
            </Box>
          )}
        </Box>

        {isRecording && (
          <LinearProgress 
            variant="determinate" 
            value={(recordingTime / currentLimits.maxDuration) * 100}
            color="error"
            sx={{ mb: 2 }}
          />
        )}
      </Paper>

      {/* Upload Section */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Option 2: Upload WAV Audio File
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>Supported format:</strong> WAV (.wav) only 
          (max {Math.round(currentLimits.maxFileSize / (1024 * 1024))}MB)
        </Typography>
        
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Important:</strong> Only WAV files are accepted. Please convert other audio formats 
            (MP3, M4A, etc.) to WAV before uploading.
          </Typography>
        </Alert>
        
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadIcon />}
          disabled={uploading}
        >
          Choose WAV File
          <input
            type="file"
            hidden
            accept=".wav,audio/wav"
            onChange={handleFileSelection}
          />
        </Button>
      </Paper>

      {/* Audio Preview - Shows selected file info */}
      {audioUrl && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle2">
              Audio Preview (WAV Format)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={audioSource === 'recording' ? 'Recorded' : 'Uploaded'} 
                size="small" 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label="WAV" 
                size="small" 
                color="success" 
                variant="outlined" 
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={isPlaying ? <PauseIcon /> : <PlayIcon />}
              onClick={togglePlayback}
              disabled={uploading}
            >
              {isPlaying ? 'Pause' : 'Play'}
            </Button>
            
            <IconButton onClick={clearAudio} disabled={uploading}>
              <DeleteIcon />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
              <VolumeIcon fontSize="small" />
              <Typography variant="caption">
                {formatTime(playbackTime)} / {formatTime(audioDuration)}
              </Typography>
            </Box>
          </Box>
          
          {/* Audio Scrubber */}
          <Slider
            value={playbackTime}
            onChange={handleSeek}
            max={audioDuration}
            step={0.1}
            disabled={uploading}
            sx={{ mb: 1 }}
          />
          
          <audio
            ref={audioRef}
            src={audioUrl}
            onEnded={handleAudioEnded}
            onLoadedMetadata={handleAudioLoadedMetadata}
            style={{ display: 'none' }}
          />
          
          <Typography variant="caption" color="text.secondary">
            File size: {(audioBlob?.size / 1024 / 1024).toFixed(2)} MB • Format: WAV • 
            Duration: {formatTime(audioDuration)}
          </Typography>
        </Paper>
      )}

      {/* Upload Progress */}
      {(uploading || isAudioUrlLoading) && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">
              {isAudioUrlLoading ? 'Preparing WAV upload...' : 'Creating voice sample...'}
            </Typography>
          </Box>
          <LinearProgress variant="indeterminate" />
        </Box>
      )}

      {/* Error Display */}
      {(error || audioUrlError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || audioUrlError?.message || 'An error occurred'}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onCancel} disabled={uploading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submitVoiceSample}
          disabled={!audioBlob || !voiceName.trim() || uploading || voiceName.length < 2 || voiceName.length > 50 || isAudioUrlLoading}
        >
          {uploading || isAudioUrlLoading ? 'Creating Voice Sample...' : 'Create Voice Sample (WAV)'}
        </Button>
      </Box>
    </Box>
  );
};

export default VoiceSampleUpload;